/**
 * User Profile Component
 * Displays user information and logout button
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logOut } from '../services/auth';
import { subscribeToFriendRequests } from '../services/friendService';
import { getRoom, createRoom } from '../services/roomService';
import FriendRequest from './FriendRequest';
import FriendList from './FriendList';
import RecentOpponents from './RecentOpponents';
import Scoreboard from './Scoreboard';
import '../styles/UserProfile.css';

function UserProfile({ user }) {
  const [loading, setLoading] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [lastRoomCode, setLastRoomCode] = useState(null);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const navigate = useNavigate();

  // Subscribe to friend requests
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToFriendRequests(user.uid, (requests) => {
      setFriendRequests(requests);
    });

    return () => unsubscribe();
  }, [user]);

  // Check for last room code
  useEffect(() => {
    const checkLastRoom = async () => {
      const savedRoomCode = localStorage.getItem('lastRoomCode');
      if (savedRoomCode) {
        try {
          const roomData = await getRoom(savedRoomCode);
          // Allow re-entering rooms that are waiting, inactive, or in any state except finished
          if (roomData && roomData.status !== 'finished') {
            setLastRoomCode(savedRoomCode);
          } else {
            localStorage.removeItem('lastRoomCode');
          }
        } catch (err) {
          localStorage.removeItem('lastRoomCode');
        }
      }
    };

    checkLastRoom();
  }, []);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logOut();
    } catch (error) {
      console.error('Logout error:', error);
      setLoading(false);
    }
  };

  const handleReenterRoom = async () => {
    if (!lastRoomCode) return;

    try {
      // Verify room still exists before navigating
      const roomData = await getRoom(lastRoomCode);
      // Allow re-entering inactive rooms for persistent room access
      if (roomData && roomData.status !== 'finished') {
        navigate(`/room/${lastRoomCode}`);
      } else {
        // Room doesn't exist or is finished - clear saved code
        localStorage.removeItem('lastRoomCode');
        setLastRoomCode(null);
        alert('This room no longer exists. Please create a new room.');
      }
    } catch (err) {
      console.error('Error checking room:', err);
      localStorage.removeItem('lastRoomCode');
      setLastRoomCode(null);
      alert('Failed to access room. Please create a new room.');
    }
  };

  const handleCreateRoom = async () => {
    setCreatingRoom(true);
    try {
      const roomCode = await createRoom(
        user.uid,
        user.displayName,
        user.photoURL
      );
      navigate(`/room/${roomCode}`);
    } catch (err) {
      console.error('Failed to create room:', err);
      alert('Failed to create room. Please try again.');
      setCreatingRoom(false);
    }
  };

  return (
    <div className="user-profile">
      <div className="profile-header">
        <div className="profile-info">
          {user.photoURL && (
            <img
              src={user.photoURL}
              alt={user.displayName}
              className="profile-photo"
            />
          )}
          <div className="profile-details">
            <h2 className="profile-name">{user.displayName || 'Anonymous'}</h2>
            <p className="profile-email">{user.email}</p>
          </div>
        </div>
        <div className="profile-actions">
          <button
            onClick={() => navigate('/settings')}
            className="settings-button"
          >
            ⚙️ Settings
          </button>
          <button
            onClick={handleLogout}
            disabled={loading}
            className="logout-button"
          >
            {loading ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      </div>

      <div className="welcome-section">
        <h1 className="welcome-title">RageBaitStudio</h1>
        <p className="welcome-message">
          Your epic multiplayer gaming platform is ready.
        </p>

        {/* Friend Requests */}
        {friendRequests.length > 0 && (
          <div className="friend-requests-section">
            <h4>Friend Requests ({friendRequests.length})</h4>
            {friendRequests.map((request) => (
              <FriendRequest
                key={request.id}
                request={request}
                currentUser={user}
              />
            ))}
          </div>
        )}

        {/* Main Sections Grid */}
        <div className="main-sections-grid">
          {/* Room Action Buttons */}
          <div className="room-actions">
            <h3>🎮 Start Playing</h3>
            <div className="action-buttons-grid">
              {lastRoomCode && (
                <button
                  className="action-button reenter-button"
                  onClick={handleReenterRoom}
                >
                  <span className="button-icon">🔄</span>
                  <span className="button-text">
                    <strong>Re-enter Room</strong>
                    <small>Return to last room</small>
                  </span>
                </button>
              )}
              <button
                className="action-button create-button"
                onClick={handleCreateRoom}
                disabled={creatingRoom}
              >
                <span className="button-icon">➕</span>
                <span className="button-text">
                  <strong>{creatingRoom ? 'Creating...' : 'Create Room'}</strong>
                  <small>Host a new game</small>
                </span>
              </button>
              <button
                className="action-button join-button"
                onClick={() => navigate('/join-room')}
              >
                <span className="button-icon">🚪</span>
                <span className="button-text">
                  <strong>Join Room</strong>
                  <small>Enter a room code</small>
                </span>
              </button>
            </div>
          </div>

          {/* Friends */}
          <div className="section-item">
            <FriendList userId={user.uid} />
          </div>

          {/* Recent Opponents */}
          <div className="section-item">
            <RecentOpponents
              userId={user.uid}
              userName={user.displayName}
              userPhoto={user.photoURL}
            />
          </div>

          {/* Scoreboard */}
          <div className="section-item">
            <Scoreboard userId={user.uid} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
